// src/components/AuthModal.tsx - Updated for new backend integration
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Google as GoogleIcon,
  // Facebook as FacebookIcon,
  GitHub as GitHubIcon,
  // PersonOutline as AnonymousIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import LegalModal from './LegalModal';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onAuthSuccess?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, onAuthSuccess }) => {
  const { 
    signInWithGoogle, 
    // signInWithFacebook, 
    signInWithGithub, 
    // signInAnonymously,
    login,
    signup,
    isAuthenticating,
    userProfile
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState<'terms' | 'privacy' | null>(null);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  // const handleSocialAuth = async (provider: 'google' | 'facebook' | 'github' | 'anonymous') => {
  const handleSocialAuth = async (provider: 'google' | 'github' ) => {
    setLoading(true);
    setError(null);
    
    try {
      switch (provider) {
        case 'google':
          await signInWithGoogle();
          break;
        // case 'facebook':
        //   await signInWithFacebook();
        //   break;
        case 'github':
          await signInWithGithub();
          break;
        // case 'anonymous':
        //   await signInAnonymously();
        //   break;
      }
      
      // Wait a moment for the backend initialization to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onAuthSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Social auth error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || (isSignUp && !displayName)) return;

    setLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        await signup(email, password, displayName);
      } else {
        await login(email, password);
      }
      
      // Wait a moment for the backend initialization to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onAuthSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Email auth error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError(null);
    setShowEmailAuth(false);
    setIsSignUp(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Show loading state when authenticating
  const isCurrentlyLoading = loading || isAuthenticating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          bgcolor: '#222222', // Match navbar dark gray/black
          color: 'white',
        }
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold" color="white">
            Get Started
          </Typography>
          <IconButton onClick={handleClose} disabled={isCurrentlyLoading} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={4}>
          Choose how you'd like to access Field Sense AI
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Show user profile info if authenticated */}
        {userProfile && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Welcome back, {userProfile.display_name || userProfile.email || 'User'}! 
            {userProfile.subscription_tier && (
              <span> ({userProfile.subscription_tier} plan)</span>
            )}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Social Auth Buttons with Native Colors */}
          <Button
            variant="contained"
            size="large"
            startIcon={isCurrentlyLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
            onClick={() => handleSocialAuth('google')}
            disabled={isCurrentlyLoading}
            sx={{ 
              justifyContent: 'flex-start', 
              pl: 3, 
              py: 1.5,
              bgcolor: '#ffffff',
              color: '#757575',
              '&:hover': { bgcolor: '#f5f5f5' },
              '&:disabled': { bgcolor: '#f5f5f5', color: '#bdbdbd' },
              '& .MuiSvgIcon-root': { color: '#4285f4' }
            }}
          >
            {isCurrentlyLoading ? 'Signing in...' : 'Continue with Google'}
          </Button>

          {/* <Button
            variant="contained"
            size="large"
            startIcon={isCurrentlyLoading ? <CircularProgress size={20} color="inherit" /> : <FacebookIcon />}
            onClick={() => handleSocialAuth('facebook')}
            disabled={isCurrentlyLoading}
            sx={{ 
              justifyContent: 'flex-start', 
              pl: 3, 
              py: 1.5,
              bgcolor: '#1877f2',
              color: '#ffffff',
              '&:hover': { bgcolor: '#166fe5' },
              '&:disabled': { bgcolor: '#166fe5', opacity: 0.6 }
            }}
          >
            {isCurrentlyLoading ? 'Signing in...' : 'Continue with Facebook'}
          </Button> */}

          <Button
            variant="contained"
            size="large"
            startIcon={isCurrentlyLoading ? <CircularProgress size={20} color="inherit" /> : <GitHubIcon />}
            onClick={() => handleSocialAuth('github')}
            disabled={isCurrentlyLoading}
            sx={{ 
              justifyContent: 'flex-start', 
              pl: 3, 
              py: 1.5,
              bgcolor: '#333333',
              color: '#ffffff',
              '&:hover': { bgcolor: '#24292e' },
              '&:disabled': { bgcolor: '#24292e', opacity: 0.6 }
            }}
          >
            {isCurrentlyLoading ? 'Signing in...' : 'Continue with GitHub'}
          </Button>

          {/* Anonymous Auth - Green accent like navbar */}
          {/* <Button
            variant="contained"
            size="large"
            startIcon={isCurrentlyLoading ? <CircularProgress size={20} sx={{ color: '#00C853' }} /> : <AnonymousIcon sx={{ color: '#00C853' }} />}
            onClick={() => handleSocialAuth('anonymous')}
            disabled={isCurrentlyLoading}
            sx={{ 
              justifyContent: 'flex-start', 
              pl: 3, 
              py: 1.5,
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: '1px solid #00C853',
              '&:hover': { bgcolor: 'rgba(0,200,83,0.1)' },
              '&:disabled': { bgcolor: 'rgba(0,200,83,0.1)', opacity: 0.6 }
            }}
          >
            <span style={{ color: 'white' }}>Try as</span>
            <span style={{ color: '#00C853', marginLeft: '0.25rem' }}> Guest</span>
            <span style={{ color: 'white', marginLeft: '0.25rem' }}>
              {isCurrentlyLoading ? ' (Signing in...)' : '(No Signup Required)'}
            </span>
          </Button> */}

          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }}>
            <Typography variant="body2" color="rgba(255,255,255,0.6)">
              or
            </Typography>
          </Divider>

          {/* Email Auth Toggle */}
          <Button
            variant="text"
            onClick={() => setShowEmailAuth(!showEmailAuth)}
            disabled={isCurrentlyLoading}
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Continue with Email & Password
          </Button>

          {/* Email Auth Form */}
          <Collapse in={showEmailAuth}>
            <Box component="form" onSubmit={handleEmailAuth} sx={{ mt: 2 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {isSignUp && (
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required={isSignUp}
                    disabled={isCurrentlyLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                        '&.Mui-focused fieldset': { borderColor: '#00C853' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                      '& .MuiOutlinedInput-input': { color: 'white' },
                    }}
                  />
                )}
                
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isCurrentlyLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#00C853' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-input': { color: 'white' },
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isCurrentlyLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#00C853' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-input': { color: 'white' },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isCurrentlyLoading || !email || !password || (isSignUp && !displayName)}
                  sx={{ 
                    py: 1.5,
                    bgcolor: '#00C853', // Green like navbar Login button
                    color: 'white',
                    '&:hover': { bgcolor: '#00A847' },
                    '&:disabled': { 
                      bgcolor: 'rgba(0, 200, 83, 0.3)', 
                      color: 'rgba(255, 255, 255, 0.5)' 
                    }
                  }}
                >
                  {isCurrentlyLoading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    isSignUp ? 'Create Account' : 'Sign In'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="text"
                  onClick={() => setIsSignUp(!isSignUp)}
                  disabled={isCurrentlyLoading}
                  sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="rgba(255,255,255,0.6)">
            By continuing, you agree to our{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => setLegalModalOpen('terms')}
              sx={{ 
                color: '#00C853', 
                textTransform: 'none', 
                p: 0, 
                minWidth: 'auto',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
              }}
            >
              Terms of Service
            </Button>
            {' '}and{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => setLegalModalOpen('privacy')}
              sx={{ 
                color: '#00C853', 
                textTransform: 'none', 
                p: 0, 
                minWidth: 'auto',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
              }}
            >
              Privacy Policy
            </Button>
          </Typography>
        </Box>
      </DialogContent>
      
      {/* Legal Modals */}
      <LegalModal
        open={legalModalOpen === 'terms'}
        onClose={() => setLegalModalOpen(null)}
        type="terms"
      />
      <LegalModal
        open={legalModalOpen === 'privacy'}
        onClose={() => setLegalModalOpen(null)}
        type="privacy"
      />
    </Dialog>
  );
};

export default AuthModal;